'use client'
// Copied from Mercury Web

import Cleave from 'cleave.js/react'
import type {Props} from 'cleave.js/react/props'
import React, {useRef} from 'react'

type MercuryCleaveProps = Props & {
  ref?:
    | React.RefObject<HTMLInputElement | null>
    | React.RefCallback<HTMLInputElement | null>
    | null
}

// Direct copy of how Cleave checks for android devices for determining onChange delay below
// https://github.com/nosir/cleave.js/blob/v1.4.10/src/utils/Util.js#L222-L224
const isAndroid = () => navigator && /android/i.test(navigator.userAgent)

// Wrapper component around the Cleave input masker plugin
const MercuryCleave = ({
  onChange,
  options,
  htmlRef,
  ref,
  ...props
}: MercuryCleaveProps) => {
  const onChangeTimerId = useRef<NodeJS.Timeout | null>(null)

  return (
    <Cleave
      {...props}
      // NB: these are two different dashes. the replacement value is the one that Cleave understands as a minus sign.
      // the Cleave library has issues with the longer dash that our own `decimalPrettifier` intentionally produces,
      // and since users could paste that in from anywhere else too, we fix it here.
      value={
        typeof props.value === 'string' ? props.value.replace('−', '-') : props.value
      }
      options={options}
      // Force Cleave to re-render when options change (Cleave does not automatically do this)
      key={JSON.stringify(options)}
      onChange={event => {
        // Reference: https://github.com/nosir/cleave.js/blob/v1.4.10/src/Cleave.react.js#L414-L420
        //
        // When viewed on an Android device, Cleave internally has a delay before setting it's internal
        // value for the input. If the onChange handler triggers any synchronous rendering, then value of
        // the target input will get out of sync, causing it to fallback to the previous value (before the change)
        //
        // The delay below waits for Cleaves internal state to settle before allowing any renders to occur
        // in the change event handler.
        if (onChange && isAndroid()) {
          event.persist()

          // Debounce onChange event if multiple inputs are triggered
          // before the timeout threshold is reached
          if (onChangeTimerId.current) {
            clearTimeout(onChangeTimerId.current)
          }
          onChangeTimerId.current = setTimeout(() => {
            onChangeTimerId.current = null
            onChange(event)
          }, 5)
        } else if (onChange) {
          onChange(event)
        }
      }}
      htmlRef={
        htmlRef ||
        (callbackRef => {
          // Cleave only supports the callback version of react ref assigning
          // so we have to figure out what type was provided to us and handle it appropriately.

          if (!ref) {
            return
          }

          if (typeof ref === 'function') {
            ref(callbackRef)
          } else {
            ref.current = callbackRef
          }
        })
      }
    />
  )
}

export default MercuryCleave
